import { Alerta, Badge, Button, Cronograma, DataList, Desplegable, Dialog, Gantt, MetricCard, Row, Select, Spinner, Stack, TextField } from "@/components";
import type { TareaCronograma } from "@/lib/api";
import { barrasRubro, posicionHoy, textoHolgura, textoPlazo, textoPredecesora, textoRubro, TIPOS_VINCULO } from "@/lib/cronograma";
import { formatFecha } from "@/lib/format";
import { useCronogramaTab } from "./useCronogramaTab";

const OPCIONES_TIPO = TIPOS_VINCULO.map((t) => ({ valor: t.valor, etiqueta: t.etiqueta }));

export function CronogramaTab() {
  const t = useCronogramaTab();
  const c = t.cronograma;
  const d = t.dialogo;
  const hoy = c ? posicionHoy(c) : null;

  const columnas = [
    {
      clave: "descripcion",
      titulo: "Ítem",
      render: (x: TareaCronograma) => (
        <Stack espacio={1}>
          <span className="whitespace-pre-line">{x.descripcion}</span>
          {!x.con_precio && <span className="text-chico text-texto-suave">Sin precio</span>}
        </Stack>
      ),
    },
    { clave: "plazo", titulo: "Cuándo", render: (x: TareaCronograma) => textoPlazo(x) },
    {
      clave: "predecesoras",
      titulo: "Después de",
      render: (x: TareaCronograma) =>
        x.predecesoras.length ? (
          <ul className="flex flex-col gap-1">
            {x.predecesoras.map((p) => (
              <li key={p.predecesora_id}>{textoPredecesora(p, t.nombres)}</li>
            ))}
          </ul>
        ) : (
          "—"
        ),
    },
    {
      clave: "holgura",
      titulo: "Holgura",
      render: (x: TareaCronograma) => (x.holgura === null ? "—" : <Badge tono={x.critico ? "peligro" : "neutro"}>{textoHolgura(x)}</Badge>),
    },
  ];

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={2}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Cronograma</h1>
        <Row envolver espacio={2}>
          <Button variante="secundario" onClick={t.abrirInicio}>
            {c?.inicio ? "Cambiar fecha de inicio" : "Fijar fecha de inicio"}
          </Button>
          <Button onClick={t.abrirNueva}>Agregar tarea</Button>
        </Row>
      </Row>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.error && <Alerta texto={t.error} />}

      {c && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard titulo="Inicio de obra" valor={c.inicio ? formatFecha(c.inicio) : "A definir"} detalle={c.inicio ? undefined : "Sin fecha, el cronograma va en días"} />
            <MetricCard titulo="Fin de obra" valor={c.fin ? formatFecha(c.fin) : "—"} />
            <MetricCard titulo="Duración" valor={`${c.duracion} días hábiles`} detalle="De lunes a viernes" />
          </div>

          <p className="text-chico text-texto-suave">
            Cada ítem del presupuesto es una tarea: cargale cuántos días hábiles dura y después de qué tareas empieza. En rojo, el
            camino crítico: si una de esas tareas se atrasa, se atrasa toda la obra. Un ítem de 0 días es un hito.
          </p>
          <Alerta
            tono="alerta"
            texto={c.sin_programar ? `${c.sin_programar} ${c.sin_programar === 1 ? "ítem no tiene" : "ítems no tienen"} duración y no ${c.sin_programar === 1 ? "está" : "están"} en el cronograma: tocá Editar para cargarla.` : null}
          />

          {c.rubros.length === 0 && <p className="text-texto-suave">Todavía no hay ítems: cargalos en Obra → Presupuesto o con Agregar tarea.</p>}

          {c.rubros.length > 0 && (
            <Row espacio={2} role="group" aria-label="Ver como">
              <Button tamanio="sm" variante={t.vista === "lista" ? "primario" : "secundario"} aria-pressed={t.vista === "lista"} onClick={() => t.elegirVista("lista")}>
                Lista
              </Button>
              <Button tamanio="sm" variante={t.vista === "gantt" ? "primario" : "secundario"} aria-pressed={t.vista === "gantt"} onClick={() => t.elegirVista("gantt")}>
                Gantt
              </Button>
            </Row>
          )}

          {t.vista === "gantt" && c.rubros.length > 0 && t.filasGantt.length === 0 && (
            <p className="text-texto-suave">Todavía no hay tareas con duración: cargala con Editar en la vista Lista.</p>
          )}
          {t.vista === "gantt" && t.filasGantt.length > 0 && (
            <Gantt
              etiqueta="Gantt de la obra"
              filas={t.filasGantt.map(({ tarea, ...f }) => (tarea ? { ...f, onElegir: () => t.abrirTarea(tarea) } : f))}
              marcas={t.marcasGantt}
              hoy={hoy}
            />
          )}

          {t.vista === "lista" && (
            <Stack espacio={3}>
              {c.rubros.map((r) => {
                const barras = barrasRubro(r, c.duracion);
                return (
                  <Desplegable
                    key={r.id}
                    abiertoInicial={r.critico}
                    titulo={
                      <Row envolver espacio={2}>
                        <span className="font-semibold">{r.nombre}</span>
                        <span className="text-chico text-texto-suave">{textoRubro(r)}</span>
                        {r.critico && <Badge tono="peligro">Crítico</Badge>}
                      </Row>
                    }
                  >
                    <Stack espacio={4}>
                      {barras.length > 0 && <Cronograma etiqueta={`Cronograma de ${r.nombre}`} etapas={barras} hoy={hoy} />}
                      <DataList
                        etiqueta={`Tareas de ${r.nombre}`}
                        columnas={columnas}
                        filas={r.tareas}
                        claveFila={(x) => x.id}
                        acciones={(x) => (
                          <Button variante="secundario" tamanio="sm" onClick={() => t.abrirTarea(x)}>
                            Editar
                          </Button>
                        )}
                      />
                    </Stack>
                  </Desplegable>
                );
              })}
            </Stack>
          )}
        </>
      )}

      <Dialog abierto={d?.tipo === "tarea"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Editar tarea" ancho="amplio">
        {d?.tipo === "tarea" && (
          <form onSubmit={(e) => void t.guardarTarea(e)}>
            <Stack espacio={4}>
              <p className="whitespace-pre-line font-semibold">{t.nombres.get(d.tarea.id)}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <TextField
                  etiqueta="Duración (días hábiles)"
                  inputMode="numeric"
                  value={t.form.duracion}
                  onChange={(e) => t.cambiar("duracion", e.target.value)}
                  error={t.errores.duracion}
                  ayuda="0 = hito. Vacío = sin programar."
                />
                <TextField
                  etiqueta="No comenzar antes del"
                  type="date"
                  value={t.form.noAntesDe}
                  onChange={(e) => t.cambiar("noAntesDe", e.target.value)}
                  error={t.errores.noAntesDe}
                />
              </div>

              <Stack espacio={3}>
                <h3 className="font-semibold">Empieza después de</h3>
                {t.form.predecesoras.length === 0 && <p className="text-chico text-texto-suave">Sin predecesoras: arranca con la obra (o en la fecha de arriba).</p>}
                {t.form.predecesoras.map((p, i) => (
                  <div key={i} className="grid gap-2 rounded-control border border-borde p-3 sm:grid-cols-[2fr_2fr_1fr_auto] sm:items-end">
                    <Select
                      etiqueta={`Tarea ${i + 1}`}
                      opciones={t.opcionesPredecesora}
                      valor={p.predecesoraId || undefined}
                      onCambio={(v) => t.cambiarPredecesora(i, { predecesoraId: v })}
                      placeholder="Escribí para buscar"
                      error={t.errores.predecesoras[i]?.predecesoraId}
                    />
                    <Select
                      etiqueta={`Cuándo ${i + 1}`}
                      opciones={OPCIONES_TIPO}
                      valor={p.tipo}
                      onCambio={(v) => t.cambiarPredecesora(i, { tipo: v === "CC" ? "CC" : "FC" })}
                    />
                    <TextField
                      etiqueta={`Espera ${i + 1} (días)`}
                      inputMode="numeric"
                      value={p.espera}
                      onChange={(e) => t.cambiarPredecesora(i, { espera: e.target.value })}
                      error={t.errores.predecesoras[i]?.espera}
                    />
                    <Button type="button" variante="fantasma" tamanio="sm" onClick={() => t.sacarPredecesora(i)}>
                      Sacar
                    </Button>
                  </div>
                ))}
                <div>
                  <Button type="button" variante="secundario" tamanio="sm" onClick={t.agregarPredecesora}>
                    Agregar predecesora
                  </Button>
                </div>
                <p className="text-chico text-texto-suave">La espera puede ser negativa para solapar (ej. −2: empieza 2 días antes).</p>
              </Stack>

              <Alerta texto={t.errorTarea} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoTarea}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "inicio"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Fecha de inicio de obra">
        {d?.tipo === "inicio" && (
          <form onSubmit={(e) => void t.guardarInicio(e)}>
            <Stack espacio={4}>
              <TextField etiqueta="Inicio de obra" type="date" value={t.inicio} onChange={(e) => t.setInicio(e.target.value)} error={t.errorInicio} ayuda="Un día hábil. Vacío = a definir." />
              <Alerta texto={t.errorGuardarInicio} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoInicio}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "nueva"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Agregar tarea">
        {d?.tipo === "nueva" && (
          <form onSubmit={(e) => void t.guardarNueva(e)}>
            <Stack espacio={4}>
              <p className="text-chico text-texto-suave">Queda como un ítem sin precio del rubro (ej. "Aprobación de planos"): no suma al presupuesto.</p>
              <Select etiqueta="Rubro" opciones={t.opcionesRubro} valor={t.nueva.rubroId || undefined} onCambio={(v) => t.cambiarNueva("rubroId", v)} error={t.erroresNueva.rubroId} />
              <TextField etiqueta="Qué es" value={t.nueva.descripcion} onChange={(e) => t.cambiarNueva("descripcion", e.target.value)} error={t.erroresNueva.descripcion} />
              <TextField etiqueta="Duración (días hábiles)" inputMode="numeric" value={t.nueva.duracion} onChange={(e) => t.cambiarNueva("duracion", e.target.value)} error={t.erroresNueva.duracion} ayuda="0 = hito" />
              <Alerta texto={t.errorNueva} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoNueva}>
                  Agregar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>
    </Stack>
  );
}
